import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { supabase, getCurrentUser } from "@/lib/supabase";
import {
  Truck,
  FileText,
  Send,
  Loader2,
  ArrowLeft,
  Clock,
  User,
  MapPin,
} from "lucide-react";

const SupplierOrderManagement = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  // Order state
  const [order, setOrder] = useState<any>(null);
  const [orderItems, setOrderItems] = useState<any[]>([]);
  const [buyer, setBuyer] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusUpdating, setStatusUpdating] = useState<boolean>(false);
  const [trackingUpdating, setTrackingUpdating] = useState<boolean>(false);
  const [messageSending, setMessageSending] = useState<boolean>(false);
  
  // Form states
  const [orderStatus, setOrderStatus] = useState<string>('');
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [shippingMethod, setShippingMethod] = useState<string>('');
  const [messageContent, setMessageContent] = useState<string>('');
  const [updateNotes, setUpdateNotes] = useState<string>('');
  
  // Load order data
  const loadOrderData = async () => {
    try {
      setLoading(true);
      
      if (!orderId) {
        console.error('No order ID provided');
        setLoading(false);
        return;
      }
      
      // Get current user
      const user = await getCurrentUser();
      if (!user) {
        console.error('No authenticated user found');
        setLoading(false);
        return;
      }
      
      // Load order details
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .eq('supplier_id', user.id)
        .single();
      
      if (orderError) {
        console.error('Error loading order:', orderError);
        setLoading(false);
        return;
      }
      
      if (!orderData) {
        console.error('Order not found or not authorized');
        setLoading(false);
        return;
      }
      
      setOrder(orderData);
      setOrderStatus(orderData.status || 'pending');
      setTrackingNumber(orderData.tracking_number || '');
      setShippingMethod(orderData.shipping_method || '');
      
      // Load order items
      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          *,
          products:product_id (
            name,
            condition,
            manufacturer,
            model
          )
        `)
        .eq('order_id', orderId);
      
      if (itemsError) {
        console.error('Error loading order items:', itemsError);
      } else {
        setOrderItems(itemsData || []);
      }
      
      // Load buyer details
      if (orderData.buyer_id) {
        const { data: buyerData, error: buyerError } = await supabase
          .from('accounts')
          .select(`
            id,
            full_name,
            email,
            phone,
            company_name,
            avatar_url
          `)
          .eq('id', orderData.buyer_id)
          .single();
        
        if (buyerError) {
          console.error('Error loading buyer:', buyerError);
        } else {
          setBuyer(buyerData);
        }
      }
      
    } catch (error) {
      console.error('Error in loadOrderData:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Update order status
  const updateOrderStatus = async () => {
    try {
      setStatusUpdating(true);
      
      const { error } = await supabase
        .from('orders')
        .update({
          status: orderStatus,
          notes: updateNotes || order.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);
      
      if (error) throw error;
      
      // Send notification to buyer about status update
      await supabase
        .from('notifications')
        .insert({
          user_id: order.buyer_id,
          title: 'Order Status Updated',
          message: `Your order #${order.order_number} status has been updated to: ${orderStatus}`,
          type: 'order_update',
          related_entity_type: 'order',
          related_entity_id: orderId
        });
      
      alert('Order status updated successfully!');
      loadOrderData(); // Reload order data
      
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status. Please try again.');
    } finally {
      setStatusUpdating(false);
      setUpdateNotes('');
    }
  };
  
  // Update shipping info
  const updateShippingInfo = async () => {
    try {
      setTrackingUpdating(true);
      
      const { error } = await supabase
        .from('orders')
        .update({
          tracking_number: trackingNumber,
          shipping_method: shippingMethod,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);
      
      if (error) throw error;
      
      // If we're adding tracking for the first time, update status to shipped
      if (order.status === 'confirmed' && trackingNumber && !order.tracking_number) {
        await supabase
          .from('orders')
          .update({
            status: 'shipped',
          })
          .eq('id', orderId);
        
        setOrderStatus('shipped');
      }
      
      // Send notification to buyer about tracking update
      await supabase
        .from('notifications')
        .insert({
          user_id: order.buyer_id,
          title: 'Shipping Information Updated',
          message: `Tracking information for your order #${order.order_number} has been updated.`,
          type: 'order_update',
          related_entity_type: 'order',
          related_entity_id: orderId
        });
      
      alert('Shipping information updated successfully!');
      loadOrderData(); // Reload order data
      
    } catch (error) {
      console.error('Error updating shipping info:', error);
      alert('Failed to update shipping information. Please try again.');
    } finally {
      setTrackingUpdating(false);
    }
  };
  
  // Send message to buyer
  const sendMessage = async () => {
    try {
      setMessageSending(true);
      
      if (!messageContent.trim()) {
        alert('Please enter a message');
        setMessageSending(false);
        return;
      }
      
      // Check if we have an inquiry associated with this order
      if (order.inquiry_id) {
        // Add message to inquiry_messages
        const user = await getCurrentUser();
        const { error } = await supabase
          .from('inquiry_messages')
          .insert({
            inquiry_id: order.inquiry_id,
            sender_id: user?.id,
            message: messageContent,
            is_read: false
          });
        
        if (error) throw error;
      } else {
        // Create a new inquiry for this order if none exists
        const user = await getCurrentUser();
        const { data: inquiryData, error: inquiryError } = await supabase
          .from('inquiries')
          .insert({
            product_id: orderItems[0]?.product_id, // Use the first product as reference
            buyer_id: order.buyer_id,
            supplier_id: user?.id,
            message: `Regarding order #${order.order_number}`,
            status: 'responded'
          })
          .select()
          .single();
        
        if (inquiryError) throw inquiryError;
        
        // Link the inquiry to the order
        await supabase
          .from('orders')
          .update({
            inquiry_id: inquiryData.id
          })
          .eq('id', orderId);
        
        // Add the message to the inquiry
        const { error: messageError } = await supabase
          .from('inquiry_messages')
          .insert({
            inquiry_id: inquiryData.id,
            sender_id: user?.id,
            message: messageContent,
            is_read: false
          });
        
        if (messageError) throw messageError;
        
        // Update local order state
        setOrder({...order, inquiry_id: inquiryData.id});
      }
      
      // Notify the buyer
      await supabase
        .from('notifications')
        .insert({
          user_id: order.buyer_id,
          title: 'New Message',
          message: `You have a new message regarding order #${order.order_number}`,
          type: 'message',
          related_entity_type: 'order',
          related_entity_id: orderId
        });
      
      alert('Message sent successfully!');
      setMessageContent('');
      
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setMessageSending(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'outline';
      case 'confirmed':
        return 'default';
      case 'shipped':
        return 'secondary';
      case 'delivered':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    loadOrderData();
  }, [orderId]);
  
  if (loading) {
    return (
      <div className="container mx-auto py-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!order) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <h2 className="text-2xl font-bold mb-4">Order Not Found</h2>
              <p className="text-muted-foreground mb-6">
                The order you're looking for doesn't exist or you don't have permission to view it.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/supplier-dashboard')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/supplier-dashboard')}
              className="mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <h1 className="text-3xl font-bold">Order #{order.order_number}</h1>
            <Badge variant={getStatusBadgeVariant(order.status)}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </Badge>
          </div>
          <p className="text-muted-foreground">
            <Clock className="h-4 w-4 inline mr-1" />
            Placed on {formatDate(order.created_at)}
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <Card>
            <CardHeader>
              <CardTitle>Order Items</CardTitle>
              <CardDescription>
                Products included in this order
              </CardDescription>
            </CardHeader>
            <CardContent>
              {orderItems.length > 0 ? (
                <div className="space-y-4">
                  {orderItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col md:flex-row justify-between p-4 border rounded-lg"
                    >
                      <div className="flex-1">
                        <h3 className="font-medium">{item.products?.name || 'Product'}</h3>
                        <p className="text-sm text-muted-foreground">
                          {item.products?.manufacturer} {item.products?.model} - {item.products?.condition}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="secondary">{item.status}</Badge>
                          <Badge variant="outline">{item.product_name}</Badge>
                        </div>
                      </div>
                      <div className="mt-2 md:mt-0 md:text-right">
                        <div className="text-sm">
                          <span className="text-muted-foreground">Qty: </span>
                          <span className="font-medium">{item.quantity}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-muted-foreground">Unit: </span>
                          <span className="font-medium">₹{item.unit_price}</span>
                        </div>
                        <div className="font-bold mt-1">₹{item.total_price}</div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="pt-4 border-t mt-4">
                    <div className="flex justify-between">
                      <span className="font-medium">Total Amount:</span>
                      <span className="font-bold text-lg">₹{order.total_amount}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-muted-foreground">No items found for this order.</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Order Management */}
          <Card>
            <CardHeader>
              <CardTitle>Order Management</CardTitle>
              <CardDescription>
                Update order status and shipping information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="status" className="w-full">
                <TabsList>
                  <TabsTrigger value="status">Status Update</TabsTrigger>
                  <TabsTrigger value="shipping">Shipping Info</TabsTrigger>
                  <TabsTrigger value="communication">Communication</TabsTrigger>
                </TabsList>
                
                {/* Status Update Tab */}
                <TabsContent value="status" className="pt-4 space-y-4">
                  <div>
                    <Label htmlFor="order-status">Order Status</Label>
                    <Select
                      value={orderStatus}
                      onValueChange={setOrderStatus}
                    >
                      <SelectTrigger id="order-status" className="w-full">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="update-notes">Update Notes (optional)</Label>
                    <Textarea
                      id="update-notes"
                      placeholder="Add any notes about this status update"
                      value={updateNotes}
                      onChange={(e) => setUpdateNotes(e.target.value)}
                      className="min-h-[100px]"
                    />
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      onClick={updateOrderStatus}
                      disabled={statusUpdating || orderStatus === order.status}
                    >
                      {statusUpdating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        'Update Status'
                      )}
                    </Button>
                  </div>
                </TabsContent>
                
                {/* Shipping Info Tab */}
                <TabsContent value="shipping" className="pt-4 space-y-4">
                  <div>
                    <Label htmlFor="shipping-method">Shipping Method</Label>
                    <Input
                      id="shipping-method"
                      placeholder="e.g., Standard Ground, Express, Freight"
                      value={shippingMethod}
                      onChange={(e) => setShippingMethod(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="tracking-number">Tracking Number</Label>
                    <Input
                      id="tracking-number"
                      placeholder="Enter tracking number"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                    />
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      onClick={updateShippingInfo}
                      disabled={trackingUpdating || 
                        (trackingNumber === order.tracking_number && 
                         shippingMethod === order.shipping_method)}
                    >
                      {trackingUpdating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Truck className="mr-2 h-4 w-4" />
                          Update Shipping Info
                        </>
                      )}
                    </Button>
                  </div>
                </TabsContent>
                
                {/* Communication Tab */}
                <TabsContent value="communication" className="pt-4 space-y-4">
                  <div>
                    <Label htmlFor="message-content">Message to Buyer</Label>
                    <Textarea
                      id="message-content"
                      placeholder="Type your message to the buyer"
                      value={messageContent}
                      onChange={(e) => setMessageContent(e.target.value)}
                      className="min-h-[150px]"
                    />
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      onClick={sendMessage}
                      disabled={messageSending || !messageContent.trim()}
                    >
                      {messageSending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {order.inquiry_id && (
                    <div className="mt-4 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/supplier/inquiries/${order.inquiry_id}`)}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        View Full Conversation
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Buyer Information */}
          <Card>
            <CardHeader>
              <CardTitle>Buyer Information</CardTitle>
            </CardHeader>
            <CardContent>
              {buyer ? (
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium">{buyer.full_name}</h3>
                      <p className="text-sm text-muted-foreground">{buyer.company_name}</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="text-muted-foreground">Email: </span>
                      <span className="font-medium">{buyer.email}</span>
                    </p>
                    {buyer.phone && (
                      <p className="text-sm">
                        <span className="text-muted-foreground">Phone: </span>
                        <span className="font-medium">{buyer.phone}</span>
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">Buyer information unavailable.</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Shipping Address */}
          <Card>
            <CardHeader>
              <CardTitle>Shipping Address</CardTitle>
            </CardHeader>
            <CardContent>
              {order.shipping_address ? (
                <div className="space-y-1">
                  <p className="font-medium">
                    {order.shipping_address.name || buyer?.full_name || 'Recipient'}
                  </p>
                  <p className="text-sm">
                    {order.shipping_address.street_address}
                  </p>
                  <p className="text-sm">
                    {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}
                  </p>
                  <p className="text-sm">
                    {order.shipping_address.country}
                  </p>
                  {order.shipping_address.phone && (
                    <p className="text-sm mt-2">
                      Phone: {order.shipping_address.phone}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <MapPin className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No shipping address provided.</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Order Date</span>
                  <span>{formatDate(order.created_at)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Payment Status</span>
                  <Badge variant={order.payment_status === 'completed' ? 'secondary' : 'outline'}>
                    {order.payment_status.charAt(0).toUpperCase() + order.payment_status.slice(1)}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Payment Method</span>
                  <span>{order.payment_method || 'Not specified'}</span>
                </div>
                
                <Separator className="my-2" />
                
                <div className="flex justify-between items-center font-bold">
                  <span>Total Amount</span>
                  <span>₹{order.total_amount}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SupplierOrderManagement;
